import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, ListOrdered } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ApiPlayground from '../components/ApiPlayground';
import TransactionLookupPlayground from '../components/TransactionLookupPlayground';
import ContainerPagination from '../components/ContainerPagination';
import DocsBreadcrumb from '../components/DocsBreadcrumb';
import KeyboardShortcutsOverlay from '../components/KeyboardShortcutsOverlay';
import BackToTop from '../components/BackToTop';

const bgImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/69qn1rnj_ABSTRACT%20DESIGN.png';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'sale-process', label: 'Sale Process' },
  { id: 'dxapi-jwt', label: 'Data Exchange API + JWT' },
  { id: 'ddc', label: 'Device Data Collection (DDC)' },
  { id: 'making-request', label: 'Making The Request', children: [
    { id: 'sample-request-url', label: 'Sample Request URL' },
    { id: 'sample-sale-request', label: 'Sample Sale Request' },
    { id: 'request-reference', label: 'Request Reference' },
  ]},
  { id: 'data-required', label: 'Data Required' },
  { id: 'available-form-fields', label: 'Available Form Fields' },
  { id: 'validation', label: 'Field Validation' },
  { id: 'sample-xml', label: 'Sample Sale XML' },
  { id: 'data-returned', label: 'Data Returned', children: [
    { id: 'response-3ds', label: 'Card Is 3D Secure' },
    { id: 'response-result', label: 'Transaction Result' },
  ]},
  { id: 'expected-responses', label: 'Expected XML Documents' },
  { id: 'transaction-lookup', label: 'Transaction Lookup', children: [
    { id: 'lookup-endpoint', label: 'Endpoint & Auth' },
    { id: 'lookup-response', label: 'Response & Fields' },
    { id: 'lookup-request-reference', label: 'Request Reference' },
  ]},
];

const flatIds = TOC.flatMap((i) => [i.id, ...(i.children?.map((c) => c.id) || [])]);

/* =================== CODE SAMPLES =================== */
const DDC_SAMPLE = `<!DOCTYPE html>
<html>
<body>
    <form id="ddc" name="ddc" method="post" action="https://staging.ecomm365.com/SecureCustomerAuthentication/API/DeviceDataCollection">
        <input type="hidden" name="Amount" value="100"/>
        <input type="hidden" name="BIN" value="411111"/>
        <input type="hidden" name="VendorId" value="100001"/>
        <input type="hidden" name="TransactionCode" value="123456-1234-1234-123456"/>
    </form>
    <script>
        window.onload = function () { document.getElementById("ddc").submit(); }
    </script>
</body>
</html>`;

const REQUEST_URL_SAMPLE = `https://staging.ecomm365.com/acqapi/service.ashx?Username=SomeName&Password=SomePassword&MessageID=30dd879c-ee2f-11db-8314-0800200c9a66&APISignature=register&Data=<R>...</R>`;

const CSHARP_SAMPLE = `[HttpPost]
public IActionResult index()
{
    HttpWebRequest httpWReq = (HttpWebRequest)WebRequest.Create("https://staging.ecomm365.com/acqapi/service.ashx"); //Create webrequest while setting the API URL to eComm staging environment
    UTF8Encoding encoding = new UTF8Encoding(); //Represents a UTF-8 encoding of Unicode characters.

    string pData = "username=SomeName"; //API Username
    pData += "&password=SomePassword"; //API Password
    pData += "&messageId=" + System.Guid.NewGuid().ToString();  //A new GUID is required for every new API Call
    pData += "&ApiSignature=Register"; //API Signature - Please notice the \`Register\` signature here for the sale request

    pData += "&Data=" + WebUtility.UrlEncode(@"<R>...</R>"); //Data fields required for request

    byte[] data = encoding.GetBytes(pData); //Getting Bytes for data

    httpWReq.Method = "POST"; //HTTP Method - POST
    httpWReq.ContentType = "application/x-www-form-urlencoded"; //Setting the correct Content Type
    httpWReq.ContentLength = data.Length; //Getting Content Length

    //Create POST data and convert it to a byte array.
    byte[] byteArray = Encoding.UTF8.GetBytes(pData);

    // Get the request stream.
    Stream dataStream = httpWReq.GetRequestStream();

    // Write the data to the request stream.
    dataStream.Write(byteArray, 0, byteArray.Length);

    // Close the Stream object.
    dataStream.Close();

    HttpWebResponse response = (HttpWebResponse)httpWReq.GetResponse(); //Assign response to \`response\` variable
    string responseString = new StreamReader(response.GetResponseStream()).ReadToEnd(); //Obtain response from post

    /* Parse XML from response */
    XmlDocument xDoc = new XmlDocument();
    xDoc.LoadXml(responseString);
    /* END */

    /* Obtain information from response fields */
    XmlNodeList ResponseR1 = xDoc.GetElementsByTagName("R1");
    XmlNodeList ResponseR2 = xDoc.GetElementsByTagName("R2");
    //... etc

    //Or loop through the elements
    /* END */

    //More code...

    return View();
}`;

const SALE_XML = `<!-- Sale Request Demonstration -->
<R>
    <R1>C</R1>
    <R2>43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R2>
    <R3>10000</R3>
    <R4>5000</R4>
    <R5>TestName</R5>
    <R6>TestMiddleName</R6>
    <R7>TestLastName</R7>
    <R8>4th Floor</R8>
    <R9>36 Carnaby Street</R9>
    <R10>London</R10>
    <R11>NA</R11>
    <R12>W1F 7DR</R12>
    <R13>UK</R13>
    <R14>123456789</R14>
    <R15>VSA</R15>
    <R16>1234567812341234</R16>
    <R17>02</R17>
    <R18>2018</R18>
    <R19>123</R19>
    <R20>826</R20>
    <R21>5000</R21>
    <R22>FirstName</R22>
    <R23>MiddleName</R23>
    <R24>LastName</R24>
    <R25>4th Floor</R25>
    <R26>36 Carnaby Street</R26>
    <R27>London</R27>
    <R28>NA</R28>
    <R29>W1F 7DR</R29>
    <R30>UK</R30>
    <R31>12346789</R31>
    <R32>678654687</R32>
    <R33>mark</R33>
    <R35>S</R35>
    <R36>test@test.com</R36>
    <R37>123.132.0.1</R37>
    <R38>*/*</R38>
    <R39>Mozilla/4.0 (compatible; Win32; WinHttp.WinHttpRequest.5)</R39>
    <R40>MerchantData</R40>
    <R41>MerchantReferenceNumber</R41>
    <R42>www.test.com/test</R42>
    <R44>0</R44>
    <R45>1000596</R45>
    <R50>35389468989</R50>
    <udf>
      <I>eCOMM</I>
      <I>435</I>
    </udf>
</R>`;

const RESP_3DS = `<!-- Sample XML Document If The Card Is 3D Secure -->
<?xml version="1.0" encoding="utf-8"?>
<R xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
    <R1>0000</R1>
    <R2>Successful</R2>
    <R3>https://1merchantacsstag.cardinalcommerce.com/MerchantACSWeb/creq.jsp</R3>
    <R4>eyJtZXNzYWdlVHlwZSI6IkNSZXEiLCJtZXNzYWdlVmVyc2lvbil6IjluMi4wliwidGhyZWVEU1NlcnZlclRyYW5zSUQiOilzZmE2MDJiMy0yOTE5LTRmY2ItYTZIZS1jMTY3Y2U5ZDVmOTUiLCJhY3NUcmFuc0lEljoiNDhkMGRmMTEtZWFhNC00OTAzLTgyMjgtN2RkNGRkMDJmOGRjIiwiY2hhbGxlbmdlV2luZG93U2l6ZSI6IjAyIn0</R4>
    <R5>7b47ecb6-058e-4355-a0c1-734c2d2fda65</R5>
    <R6>61c5580b-dab9-4305-bc26-b8f9d2212b6f</R6>
    <R7>6gUaDtGDeg96vk3pJrg1</R7>
    <CardHolderInfo />
</R>`;

const RESP_CONFIRM = `<!-- Notification Method Set: Notification, Confirmation will be sent to Notification URL -->
<?xml version="1.0" encoding="utf-8"?>
<R xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
    <R1>0008</R1>
    <R2>Confirmation received</R2>
</R>`;

const RESP_RESULT = `<!-- Sample XML Document — Transaction Result -->
<?xml version="1.0" encoding="utf-8"?>
<R xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
    <R1>0044</R1>
    <R2>Successful</R2>
    <R3>87b1ecbb-1ef2-4969-98f5-d68d81e7c0d3</R3>
    <R4>Successful</R4>
    <R5>Data Not Checked</R5>
    <R8>Match</R8>
    <R9>Ok</R9>
    <R10>Confirm</R10>
    <R13>Transaction successful</R13>
    <udf>
        <I>Test</I>
        <I>MasterCard</I>
    </udf>
    <R14>85234706069500000000065</R14>
    <R15>1312836</R15>
    <SchemeResponseCode>00</SchemeResponseCode>
    <ProcessorMessage>Transaction successful</ProcessorMessage>
</R>`;

const DXAPI_REQUEST = `{
  "transactionCode": "87b4562872b640989f4fc645d5f84e89",
  "vendorId": "1000000",
  "pan": "5200000000112151",
  "cardType": "Mastercard",
  "acquirerCountryCode": "372"
}`;

const DXAPI_RESPONSE = `{
  "ErrorNumber": 0,
  "ErrorDescription": "Success",
  "RequestId": "b3933183-48df-409f-94ff-12952364009b",
  "Payload": {
    "Account": {
      "CardBrand": "Visa",
      "LastFour": "2701",
      "FIDO": {
        "FlowType": "ENROLLMENT"
      }
    },
    "Issuer": {
      "SupportedVersions": [
        {
          "Version": "2.1.0",
          "Capabilities": ["AuthenticationAvailableAtACS", "DAF"],
          "MethodURLPresent": true
        },
        {
          "Version": "2.2.0",
          "Capabilities": [
            "AuthenticationAvailableAtACS",
            "DecoupledAuthentication",
            "DataOnly",
            "DelegatedAuthentication",
            "IssuerTRA",
            "DAF"
          ],
          "MethodURLPresent": true
        }
      ]
    }
  },
  "ReferenceId": "51ca6679-12ed-47c4-8982-1a29e10d4587",
  "ReasonCode": "0",
  "ReasonDescription": "Success"
}`;

/* =================== DATA TABLES =================== */
const DDC_FIELDS = [
  { name: 'Amount', desc: 'Total transaction amount without decimals (e.g. €100.00 => 10000, €123.67 => 12367, €0.99 => 99).', req: 'Y', def: 'N(20)' },
  { name: 'BIN', desc: 'Bank Identification Number = first 6 digits of the card number.', req: 'Y', def: 'N(6)' },
  { name: 'VendorId', desc: 'eCOMM provided Vendor ID (VID).', req: 'Y', def: 'AN(50)' },
  { name: 'TransactionCode', desc: 'Merchant provided value that is unique for each transaction.', req: 'Y', def: 'AN(40)' },
];

const DXAPI_REQUEST_FIELDS = [
  { name: 'TransactionCode', req: 'Y', type: 'String', desc: 'A unique Identifier generated for each transaction.' },
  { name: 'VendorId', req: 'Y', type: 'String', desc: 'eCOMM-provided Vendor ID.' },
  { name: 'Pan', req: 'Y', type: 'String', desc: 'The full PAN of the cardholder between 13–19 digits.' },
  { name: 'CardType', req: 'Y', type: 'String', desc: 'Type of card used. Possible values: MASTERCARD, PAYPAK, RUPAY, UPI, VISA.' },
  { name: 'AcquirerCountryCode', req: 'Y', type: 'String', desc: 'Must align with ISO 3166-1.' },
];

const DXAPI_RESPONSE_FIELDS = [
  { name: 'ErrorNumber', req: 'Y', type: 'String', desc: 'Application error number. Non-zero indicates an error processing the request.' },
  { name: 'ErrorDescription', req: 'Y', type: 'String', desc: 'Error description. Possible values: 0 = Success, 2000 = Account Number not valid, 1000 = Service error.' },
  { name: 'RequestId', req: 'Y', type: 'String', desc: 'Request identifier returned from Cardinal.' },
  { name: 'Payload', req: 'Y', type: 'Object', desc: 'Contains ReferenceId, Issuer object, and Account object.' },
];

const FORM_FIELDS = [
  { name: 'R1', desc: 'Transaction Type: Sale = [C]. Sale with Pre-Auth = [CA].', req: 'Y', def: 'AN(2)' },
  { name: 'R2', desc: 'Transaction Code. Merchant must provide a unique code for each transaction.', req: 'Y', def: 'AN(40)' },
  { name: 'R3', desc: 'Total Transaction Amount (without decimals). E.g. €100.00 = [10000], €0.99 = [99].', req: 'Y', def: 'N(20)' },
  { name: 'R4', desc: 'Tax Amount (without decimals).', req: 'Y', def: 'N(20)' },
  { name: 'R5', desc: 'Billing First Name.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R6', desc: 'Billing Middle Name.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R7', desc: 'Billing Last Name.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R8', desc: 'Billing Address Line 1.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R9', desc: 'Billing Address Line 2.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R10', desc: 'Billing City.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R11', desc: 'Billing State / Province.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R12', desc: 'Billing Postal Code.', req: 'Y/AVS', def: 'AN(10)' },
  { name: 'R13', desc: 'Billing Country Code (ISO 3166). E.g. Ireland = [IE], UK = [UK].', req: 'Y/AVS', def: 'AN(3)' },
  { name: 'R14', desc: 'Billing Phone Number (without hyphens). E.g. 5555555555.', req: 'Y/AVS', def: 'N(20)' },
  { name: 'R15', desc: 'Card Type: Visa = [VSA], MasterCard = [MSC], Maestro = [MAE], Amex = [AMX].', req: 'Y', def: 'AN(20)' },
  { name: 'R16', desc: 'Card Number.', req: 'Y', def: 'N(19)' },
  { name: 'R17', desc: 'Card Expiration Month (MM). E.g. [01].', req: 'Y', def: 'N(2)' },
  { name: 'R18', desc: 'Card Expiration Year (YYYY). E.g. [2028].', req: 'Y', def: 'N(4)' },
  { name: 'R19', desc: 'Card CVV.', req: 'Y', def: 'N(3)' },
  { name: 'R20', desc: 'Numeric Currency Code (ISO 4217). E.g. EUR = [978], GBP = [826].', req: 'Y', def: 'N(3)' },
  { name: 'R21', desc: 'Shipping Amount (without decimals).', req: 'Y', def: 'N(20)' },
  { name: 'R22', desc: 'Shipping First Name.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R23', desc: 'Shipping Middle Name.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R24', desc: 'Shipping Last Name.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R25', desc: 'Shipping Address Line 1.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R26', desc: 'Shipping Address Line 2.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R27', desc: 'Shipping City.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R28', desc: 'Shipping State / Province.', req: 'Y/AVS', def: 'AN(50)' },
  { name: 'R29', desc: 'Shipping Postal Code.', req: 'Y/AVS', def: 'AN(10)' },
  { name: 'R30', desc: 'Shipping Country Code (ISO 3166).', req: 'Y/AVS', def: 'AN(3)' },
  { name: 'R31', desc: 'Shipping Phone Number (without hyphens).', req: 'Y/AVS', def: 'N(20)' },
  { name: 'R32', desc: 'Order Number or Transaction Identifier.', req: 'Y', def: 'AN(50)' },
  { name: 'R33', desc: 'Order Channel: [MARK], [CART], [CALLCENTER], [WIDGET], [PRODUCT], [1CLICK].', req: 'Y', def: 'A(16)' },
  { name: 'R35', desc: 'Transaction Mode: MOTO = [M], e-Commerce = [S].', req: 'Y', def: 'A(1)' },
  { name: 'R36', desc: "Consumer's Email Address.", req: 'N', def: 'AN(255)' },
  { name: 'R37', desc: 'Consumer IP Address (IPv4). E.g. [80.41.105.12].', req: 'N', def: 'AN(15)' },
  { name: 'R38', desc: 'Exact content of the HTTP Accept header.', req: 'Y', def: 'AN(256)' },
  { name: 'R39', desc: 'Exact content of the HTTP User-Agent header.', req: 'Y', def: 'AN(500)' },
  { name: 'R40', desc: 'Merchant Data (returned in the transaction result).', req: 'N', def: 'AN(20)' },
  { name: 'R41', desc: 'Merchant Reference Number (returned in the transaction result).', req: 'N', def: 'AN(20)' },
  { name: 'R42', desc: 'Notification URL (sends confirmation data). E.g. http://Domain.com/NotificationURL/', req: 'Y', def: 'AN(2000)' },
  { name: 'R44', desc: '3D Secure mode: [0] default, [1] force check, [2] skip check, [3] force check but always obtain auth.', req: 'N', def: 'N(1)' },
  { name: 'R45', desc: 'Vendor ID (VID).', req: 'Y', def: 'AN(50)' },
  { name: 'R46', desc: 'Originating Merchant URL — only required for PSPs.', req: 'N', def: 'AN(2000)' },
  { name: 'R47', desc: 'Originating Merchant IP — only required for PSPs.', req: 'N', def: 'AN(15)' },
  { name: 'R50', desc: 'Mobile Phone Number — Required for 3DS v2.x.', req: 'Y', def: 'AN(15)' },
  { name: 'udf', desc: 'UDF List (see Overview).', req: 'N', def: 'AN(50)' },
];

const VALIDATION = [
  { name: 'R1', desc: 'Transaction Type.', regex: '^(C|D|CA|DA)$' },
  { name: 'R2', desc: 'Transaction Code.', regex: '^[-_0-9A-Za-z]{0,40}$' },
  { name: 'R3', desc: 'Amount.', regex: '^[0-9]{0,20}$' },
  { name: 'R4', desc: 'Tax Amount.', regex: '^[0-9]{0,20}$' },
  { name: 'R5', desc: 'Billing First Name.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R6', desc: 'Billing Middle Name.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R7', desc: 'Billing Last Name.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R8', desc: 'Billing Address Line 1.', regex: "^[a-zA-Z0-9\"/\\s,()'#.`-]{1,255}$" },
  { name: 'R9', desc: 'Billing Address Line 2.', regex: "^[a-zA-Z0-9\"/\\s,()'#.`-]{0,255}$" },
  { name: 'R10', desc: 'Billing City.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R11', desc: 'Billing State / Province.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R12', desc: 'Billing Postal Code.', regex: "^[a-zA-Z0-9 '.,-]{1,10}$" },
  { name: 'R13', desc: 'Billing Country Code.', regex: '^[a-zA-Z]{2,3}$' },
  { name: 'R14', desc: 'Billing Phone Number.', regex: '^[0-9]{1,20}$' },
  { name: 'R15', desc: 'Card Type.', regex: '^[a-zA-Z0-9]{1,20}$' },
  { name: 'R16', desc: 'Card Number.', regex: 'Luhn Algorithm' },
  { name: 'R17', desc: 'Card Expiry Month.', regex: '^((0[1-9])|(1[0-2]))$' },
  { name: 'R18', desc: 'Card Expiry Year.', regex: '^(20)?(([1-2][0-9]))$' },
  { name: 'R19', desc: 'CVV.', regex: '^[0-9]{3}$' },
  { name: 'R20', desc: 'Numeric Currency Code.', regex: '^[0-9]{3}$' },
  { name: 'R21', desc: 'Shipping Amount.', regex: '^[0-9]{0,20}$' },
  { name: 'R22', desc: 'Shipping First Name.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R23', desc: 'Shipping Middle Name.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R24', desc: 'Shipping Last Name.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R25', desc: 'Shipping Address Line 1.', regex: "^[a-zA-Z0-9\"/\\s,()'#.`-]{1,255}$" },
  { name: 'R26', desc: 'Shipping Address Line 2.', regex: "^[a-zA-Z0-9\"/\\s,()'#.`-]{0,255}$" },
  { name: 'R27', desc: 'Shipping City.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R28', desc: 'Shipping State / Province.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R29', desc: 'Shipping Postal Code.', regex: "^[a-zA-Z0-9 '.,-]{1,10}$" },
  { name: 'R30', desc: 'Shipping Country Code.', regex: '^[a-zA-Z]{2,3}$' },
  { name: 'R31', desc: 'Shipping Phone Number.', regex: '^[0-9]{1,20}$' },
  { name: 'R32', desc: 'Order Number.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R33', desc: 'Order Channel.', regex: '\\b(mark|cart|callcenter|widget|product|1click)\\b' },
  { name: 'R35', desc: 'Transaction Mode.', regex: '\\b(M|S|m|s){1}\\b' },
  { name: 'R36', desc: 'Email Address.', regex: '(^$|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$)' },
  { name: 'R37', desc: 'Consumer IP.', regex: '^[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}$' },
  { name: 'R38', desc: 'HTTP Accept Header.', regex: "^[a-zA-Z0-9\\s,()./*+'\\[\\]_:;=-]{1,400}$" },
  { name: 'R39', desc: 'User Agent Header.', regex: '^(?=.*?[A-Z])(?=.*?[a-z]).{8,500}$' },
  { name: 'R40', desc: 'Merchant Data.', regex: '^$|^[a-zA-Z0-9|]{1,20}$' },
  { name: 'R41', desc: 'Merchant Reference Number.', regex: '^$|^[a-zA-Z0-9]{1,20}$' },
  { name: 'R42', desc: 'Notification URL.', regex: 'https?:\\/\\/?[-a-zA-Z0-9@:%._\\+\\~#=]{2,256}\\b(...)' },
  { name: 'R44', desc: 'Apply 3D Secure.', regex: '^$|^[0-3]{1}$' },
  { name: 'R45', desc: 'Vendor ID (VID).', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R47', desc: 'Originating Merchant IP.', regex: '^[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}$' },
  { name: 'R50', desc: 'Mobile Phone Number.', regex: '^[0-9]{1,20}$' },
];

const RESP_3DS_FIELDS = [
  { name: 'R1', desc: (<>Error code (see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link>).</>), def: 'N(4)' },
  { name: 'R2', desc: 'Description.', def: 'AN(250)' },
  { name: 'R3', desc: 'ACSURL — Link to the 3D Secure service.', def: 'AN(2048)' },
  { name: 'R4', desc: 'PAReq — Message passed to the Issuing Bank.', def: 'AN(2048)' },
  { name: 'R5', desc: 'MD — Secret key between the Issuer, Acquirer and Merchant.', def: 'AN(2048)' },
  { name: 'R6', desc: "Merchant's Transaction Code.", def: 'AN(40)' },
  { name: 'R7', desc: 'Cardinal Transaction Reference Id.', def: 'AN(20)' },
];

const RESP_RESULT_FIELDS = [
  { name: 'R1', desc: (<>Error code (see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link>).</>), def: 'N(4)' },
  { name: 'R2', desc: 'Description.', def: 'AN(250)' },
  { name: 'R3', desc: "Merchant's own reference code.", def: 'AN(40)' },
  { name: 'R4', desc: 'Transaction status.', def: 'AN(20)' },
  { name: 'R5', desc: 'AVS / CV2 overall result (All Match, Security Code Match Only, Address Match Only, No Data Matches, Data Not Checked).', def: 'AN(50)' },
  { name: 'R6', desc: "Address numeric match from AVS/CV2 (Match / Not Match).", def: 'AN(20)' },
  { name: 'R7', desc: 'Postcode match from AVS/CV2 (Match / Not Match).', def: 'AN(20)' },
  { name: 'R8', desc: 'CV2 code match from AVS/CV2 (Match / Not Match).', def: 'AN(20)' },
  { name: 'R9', desc: '3D Secure check result (Ok / Error).', def: 'AN(50)' },
  { name: 'R10', desc: 'Confirmation Type (Confirm / Authorisation).', def: 'AN(10)' },
  { name: 'R11', desc: 'Merchant specified data returned on response.', def: 'AN(20)' },
  { name: 'R12', desc: 'Merchant specified transaction identifier returned on response.', def: 'AN(20)' },
  { name: 'udf', desc: 'UDF List (see Overview).', def: 'AN(50)' },
];

/* =================== HELPER COMPONENTS =================== */
const CodeBlock = ({ code, lang = 'csharp', testid }) => (
  <div className="relative bg-[#1e1e1e] rounded-lg overflow-hidden border border-gray-700 my-4" data-testid={testid}>
    <div className="flex items-center justify-between px-4 py-2 bg-[#2d2d2d] border-b border-gray-700">
      <span className="text-xs font-mono text-gray-400 uppercase tracking-wide">{lang}</span>
      <button
        onClick={() => navigator.clipboard?.writeText(code)}
        className="text-xs text-gray-400 hover:text-white transition-colors duration-150 font-medium"
        data-testid={`${testid}-copy`}
      >
        Copy
      </button>
    </div>
    <pre className="overflow-x-auto p-4 text-sm leading-relaxed">
      <code className={`language-${lang} text-gray-100 font-mono whitespace-pre`}>{code}</code>
    </pre>
  </div>
);

const ReqBadge = ({ value }) => {
  const styles =
    value === 'Y'
      ? 'bg-green-100 text-green-800 border-green-200'
      : value === 'N'
      ? 'bg-gray-100 text-gray-700 border-gray-200'
      : 'bg-amber-100 text-amber-800 border-amber-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${styles}`}>
      {value}
    </span>
  );
};

/* =================== PAGE =================== */
const SalePreAuth = () => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(flatIds[0]);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offset = 140;
      let current = flatIds[0];
      for (const id of flatIds) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - offset <= 0) current = id;
      }
      setActiveId(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setMobileTocOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col scroll-smooth" data-testid="sale-preauth-page">
      <Header />
      <div className="h-20" />

      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <DocsBreadcrumb section="api-reference" currentId="sale-pre-auth" />
          <button
            onClick={() => navigate('/technical-documentation/api-reference')}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid="sale-preauth-back-button"
          >
            <ArrowLeft size={18} />
            Back to API Reference
          </button>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* ============ MAIN CONTENT ============ */}
            <main className="flex-1 min-w-0 lg:order-1">
              {/* Mobile collapsed TOC pill */}
              <div className="lg:hidden mb-6 border border-gray-200 rounded-xl overflow-hidden bg-white/90 backdrop-blur-sm">
                <button
                  type="button"
                  onClick={() => setMobileTocOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-left"
                  aria-expanded={mobileTocOpen}
                  data-testid="mobile-toc-toggle"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-[#0a1929]">
                    <ListOrdered size={16} className="text-[#ef5b25]" />
                    On this page
                    <span className="text-gray-400 font-normal">({flatIds.length})</span>
                  </span>
                  <ChevronDown
                    size={18}
                    className={`text-gray-500 transition-transform ${mobileTocOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {mobileTocOpen && (
                  <nav className="bg-white max-h-[60vh] overflow-y-auto py-2" data-testid="mobile-toc-panel">
                    {TOC.map((item) => (
                      <div key={item.id}>
                        <a
                          href={`#${item.id}`}
                          onClick={(e) => handleNavClick(e, item.id)}
                          className={`block px-4 py-2 text-sm transition-colors duration-150 border-l-2 ${
                            activeId === item.id
                              ? 'border-[#1F4E79] bg-blue-50 text-[#1F4E79] font-semibold'
                              : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                          }`}
                          data-testid={`m-toc-link-${item.id}`}
                        >
                          {item.label}
                        </a>
                        {item.children && (
                          <div className="ml-3">
                            {item.children.map((child) => (
                              <a
                                key={child.id}
                                href={`#${child.id}`}
                                onClick={(e) => handleNavClick(e, child.id)}
                                className={`block px-4 py-1.5 text-xs transition-colors duration-150 border-l-2 ${
                                  activeId === child.id
                                    ? 'border-[#1F4E79] bg-blue-50/60 text-[#1F4E79] font-semibold'
                                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79]'
                                }`}
                                data-testid={`m-toc-link-${child.id}`}
                              >
                                {child.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </nav>
                )}
              </div>

              {/* Title */}
              <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3" data-testid="sale-preauth-title">
                  Sale &amp; Pre-Auth
                </h1>
                <p className="text-gray-600 text-lg">eCOMM API — the most flexible integration option for processing payments.</p>
                <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="api-signature-chips">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">API Signature</span>
                  <code
                    className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-900 text-[#85ea2d] text-xs font-mono font-semibold shadow-sm border border-gray-800"
                    data-testid="api-signature-sale"
                  >
                    APISignature=sale
                  </code>
                </div>
              </div>

              {/* Overview */}
              <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Overview</h2>
                <div className="space-y-4 text-gray-700 text-base leading-relaxed">
                  <p>
                    Our <strong>Sale</strong> API is the most flexible integration option out there. The <strong>Sale</strong> API allows the merchant to fully control the flow over the entire payment process — allowing the merchant to provide a unique experience by designing their own payments page while also handling the payments process.
                  </p>
                  <p>
                    The information entered into the transaction form on the merchant site's payments page is collected and sent in XML format to the API by HTTP POST. This information is encapsulated in a parameter field called <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> when making the request.
                  </p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mt-6">
                  <p className="text-gray-800 text-sm font-semibold mb-2">Please Note The Following:</p>
                  <ol className="text-gray-700 text-sm space-y-2 list-decimal pl-5">
                    <li>The merchant will need to obtain the IP address of the consumer. Once obtained, please enter the IP in <strong>Field R37</strong> in IPv4 format (e.g. 80.41.105.12).</li>
                    <li><strong>Fields R5 → R14 &amp; R22 → R31</strong> are only required if AVS is enabled on the merchant's account.</li>
                    <li>If the merchant does not require specific 'required' fields, the merchant can pass 'NA'-type data to pass validation such as 'NA' for Alpha or '1234' for Numeric — however, we recommend passing as much information as possible.</li>
                    <li>
                      <strong>Field R15</strong>, the merchant can determine this by using the first digit of the card number. The merchant must pass <strong>[VSA]</strong>, <strong>[MSC]</strong>, <strong>[MAE]</strong> or <strong>[AMX]</strong> when populating the R15 field:
                      <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li>First digit <strong>3</strong> = <strong>[AMX]</strong> (American Express)</li>
                        <li>First digit <strong>4</strong> = <strong>[VSA]</strong> (Visa)</li>
                        <li>First digit <strong>5</strong> = <strong>[MSC]</strong> (MasterCard)</li>
                        <li>First digit <strong>6</strong> = <strong>[MAE]</strong> (Maestro)</li>
                      </ul>
                    </li>
                  </ol>
                </div>
              </section>

              {/* Sale Process */}
              <section id="sale-process" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Sale Process</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-4">
                  The process of the Sale API is simple and straight forward. Below demonstrates the steps required in order to successfully process a sale.
                </p>
                <ol className="list-decimal pl-6 space-y-3 text-gray-700 text-base leading-relaxed">
                  <li>
                    <strong>[3DS v2.x only]</strong> Before sending Sale or Pre-Auth request, merchant needs to perform <strong>DXAPI</strong> AND <strong>Device Data Collection (DDC)</strong>.
                  </li>
                  <li>
                    The Consumer or Merchant determines if the transaction will be a <strong>Sale</strong> or a <strong>Pre-Auth</strong>. If the transaction is a Sale, provide <strong>"C"</strong> in the R1 Field of the XML Document; if Pre-Auth, provide <strong>"CA"</strong> in R1.
                  </li>
                  <li>
                    The collected form field data will be supplied into the XML Document fields for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter of the POST request. Once submitted, the request will be sent to us and we will respond with information to determine the next step of that transaction.
                  </li>
                  <li>
                    Using the <a href="#expected-responses" onClick={(e) => handleNavClick(e, 'expected-responses')} className="text-[#1F4E79] underline hover:text-[#163a5f]">Expected XML Documents</a> as a guideline as to what results the merchant can expect, gives the merchant the information required for the next steps for that transaction.
                  </li>
                  <li>
                    If the transaction was a Pre-Auth, the funds will be frozen in a "Pending" state within the customer's bank account. The merchant must then use our <span className="text-gray-400 italic">Complete Pre-Auth</span> API to release the funds from the consumer's account.
                  </li>
                </ol>
              </section>

              {/* DXAPI + JWT */}
              <section id="dxapi-jwt" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <div className="flex items-start gap-3 mb-4">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Data Exchange API + JWT</h2>
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-[#1F4E79] text-white uppercase tracking-wide">Recommended</span>
                </div>
                <p className="text-gray-700 text-base leading-relaxed mb-4">
                  This option lets the integrator pass the full Card Number in an API call to set up the Device Data Collection session. In return, you'll receive a <strong>ReferenceId</strong> to use on the web frontend instead of the Card BIN. Along with the ReferenceId, you'll receive additional data points for the Card Number that can help with authentication.
                </p>

                <h3 className="text-lg font-bold text-gray-900 mb-2 mt-6">Integration Steps</h3>
                <ol className="list-decimal pl-6 space-y-2 text-gray-700 text-sm">
                  <li>Cardinal Cruise Account Credentials</li>
                  <li>
                    Data Exchange API
                    <ul className="list-disc pl-5 mt-1 space-y-1 text-gray-600">
                      <li>Connecting to the Data Exchange API</li>
                      <li>Data Exchange Request Field Names</li>
                      <li>Handling the Data Exchange Response with ReferenceId</li>
                    </ul>
                  </li>
                  <li>
                    Cardinal Cruise API — ReferenceId passed in JWT
                    <ul className="list-disc pl-5 mt-1 space-y-1 text-gray-600">
                      <li>Generating an Authentication JWT with ReferenceId</li>
                      <li>Initiating the DeviceDataCollectionUrl</li>
                      <li>Handle the Response from the Device Data Collection URL</li>
                    </ul>
                  </li>
                </ol>

                <h3 className="text-lg font-bold text-gray-900 mb-2 mt-8">Request Fields</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-[#1F4E79] text-white">
                        <th className="text-left p-3">Field</th>
                        <th className="text-left p-3">Required</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-left p-3">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DXAPI_REQUEST_FIELDS.map((f, i) => (
                        <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                          <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                          <td className="p-3"><ReqBadge value={f.req} /></td>
                          <td className="p-3 font-mono text-xs">{f.type}</td>
                          <td className="p-3">{f.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2 mt-8">Response Fields</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-[#1F4E79] text-white">
                        <th className="text-left p-3">Field</th>
                        <th className="text-left p-3">Required</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-left p-3">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DXAPI_RESPONSE_FIELDS.map((f, i) => (
                        <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                          <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                          <td className="p-3"><ReqBadge value={f.req} /></td>
                          <td className="p-3 font-mono text-xs">{f.type}</td>
                          <td className="p-3">{f.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2 mt-8">Payload — Account &amp; Issuer Objects</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                    <p className="font-semibold text-[#1F4E79] mb-2">Account Object</p>
                    <ul className="text-sm text-gray-700 space-y-1 list-disc pl-5">
                      <li><strong>LastFour</strong> — Last four digits of the AccountNumber from the request.</li>
                      <li><strong>CardBrand</strong> — Type of card used for the purchase.</li>
                      <li>
                        <strong>FIDO</strong> sub-object — <code className="text-xs">FlowType</code> (ENROLLMENT / AUTHENTICATION / FAILURE), <code className="text-xs">ReasonCode</code>, <code className="text-xs">ReasonDescription</code>.
                      </li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                    <p className="font-semibold text-[#1F4E79] mb-2">Issuer Object</p>
                    <ul className="text-sm text-gray-700 space-y-1 list-disc pl-5">
                      <li><strong>SupportedVersions</strong> — EMV 3DS versions the Issuer supports.</li>
                      <li><strong>Version</strong> — Active 3DS protocol version supported by the Issuer ACS.</li>
                      <li><strong>Capabilities</strong> — Authentication options (AuthenticationAvailableAtACS, DecoupledAuthentication, TrustlistSupport, etc.).</li>
                      <li><strong>MethodURLPresent</strong> — Whether a 3DS Method is associated with the Issuer Range.</li>
                    </ul>
                  </div>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2 mt-8">Sample Request</h3>
                <CodeBlock code={DXAPI_REQUEST} lang="json" testid="dxapi-request-code" />

                <h3 className="text-lg font-bold text-gray-900 mb-2 mt-6">Sample Response</h3>
                <CodeBlock code={DXAPI_RESPONSE} lang="json" testid="dxapi-response-code" />
              </section>

              {/* DDC */}
              <section id="ddc" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Device Data Collection — DDC (3DS v2.x only)</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-3">
                  If merchant is using 3DS version 2.x (EMV 3DS), the process starts with collecting data from a device used by the customer to perform the transaction.
                </p>
                <p className="text-gray-700 text-base leading-relaxed mb-3">
                  As soon as customer inputs the first 6 digits (BIN) of the card number on the merchant's website, the merchant should create a hidden HTML IFrame containing a form and <strong>POST</strong> it to our DDC endpoint. Our system will then perform DDC — no further action required from the merchant.
                </p>
                <p className="text-gray-700 text-base leading-relaxed mb-6">
                  <strong>Production URL</strong> for the DDC endpoint will be provided when needed.
                </p>

                <h3 className="text-lg font-bold text-gray-900 mb-2">DDC Form Parameters</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-[#1F4E79] text-white">
                        <th className="text-left p-3">Field</th>
                        <th className="text-left p-3">Description</th>
                        <th className="text-left p-3">Required</th>
                        <th className="text-left p-3">Definition</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DDC_FIELDS.map((f, i) => (
                        <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                          <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                          <td className="p-3">{f.desc}</td>
                          <td className="p-3"><ReqBadge value={f.req} /></td>
                          <td className="p-3 font-mono text-xs">{f.def}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2 mt-6">DDC Form Example</h3>
                <CodeBlock code={DDC_SAMPLE} lang="html" testid="ddc-sample-code" />
              </section>

              {/* Making The Request */}
              <section id="making-request" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Executing Sale or Pre-Auth Request</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-4">
                  HTTP is used as the request/response protocol between a merchant's site and the eCOMM API. In the back end, a merchant submits a HTTP POST request to the eCOMM server, which returns an XML document where the merchant must cater for the action listed — 3D Secure, Declined, or a Successful Transaction.
                </p>
                <p className="text-gray-700 text-base leading-relaxed mb-3">The request string for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Sale</code> call must be composed of the following information:</p>
                <ol className="list-decimal pl-6 space-y-1 text-gray-700 text-sm mb-6">
                  <li><strong>Username</strong> = SomeName</li>
                  <li><strong>Password</strong> = SomePassword</li>
                  <li><strong>MessageID</strong> = <em>*GUID (e.g. 30dd879c-ee2f-11db-8314-0800200c9a66)</em></li>
                  <li><strong>APISignature</strong> = Register</li>
                  <li><strong>Data</strong> = Form data in XML format (<a href="#sample-xml" onClick={(e) => handleNavClick(e, 'sample-xml')} className="text-[#1F4E79] underline">see sample here</a>)</li>
                </ol>

                <div id="sample-request-url" className="scroll-mt-28">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">A fully formatted HTTP POST request should look like this:</h3>
                  <CodeBlock code={REQUEST_URL_SAMPLE} lang="http" testid="request-url-code" />
                </div>

                {/* Sample Sale Request with Postman/OpenAPI buttons */}
                <div id="sample-sale-request" className="scroll-mt-28 mt-8">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h3 className="text-xl font-bold text-gray-900">Sample Sale Request</h3>
                    <div className="flex flex-wrap gap-2">
                      <a
                        href="/assets/ecomm-api.postman_collection.json"
                        download="ecomm-api.postman_collection.json"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-[#ef5b25] hover:bg-[#d14f1f] text-white text-xs font-semibold rounded-md shadow-sm transition-colors duration-150"
                        data-testid="download-postman-btn"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M13.52 10.2l-2.9 2.9-.72-.73c-.14-.15-.38-.15-.52 0-.15.14-.15.38 0 .52l.98.99c.08.07.18.11.27.11.1 0 .19-.04.26-.11l3.16-3.16c.14-.15.14-.38 0-.52-.15-.15-.38-.15-.53 0zM12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm0 22C6.48 22 2 17.52 2 12S6.48 2 12 2s10 4.48 10 10-4.48 10-10 10z"/>
                        </svg>
                        Download Postman Collection
                      </a>
                      <a
                        href="/assets/ecomm-api-openapi.yaml"
                        download="ecomm-api-openapi.yaml"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-[#85ea2d] hover:bg-[#6ec723] text-gray-900 text-xs font-semibold rounded-md shadow-sm transition-colors duration-150"
                        data-testid="download-openapi-btn"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                        </svg>
                        Download OpenAPI Spec
                      </a>
                    </div>
                  </div>
                  <CodeBlock code={CSHARP_SAMPLE} lang="csharp" testid="sample-sale-request-code" />
                </div>

                {/* Request Reference */}
                <div id="request-reference" className="scroll-mt-28 mt-10">
                  <ApiPlayground
                    apiSignature="Register"
                    title="Request Reference — Run a Sale"
                    description="Sample XML to register a new Sale transaction."
                    defaultData={SALE_XML.replace('<!-- Sale Request Demonstration -->\n', '')}
                  />
                </div>
              </section>

              {/* Data Required */}
              <section id="data-required" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Data Required</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-3">
                  When designing the payment form, the merchant needs to consider each field in the table below. In the XML document, the parent tag is called <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R&gt;</code> and each child tag is called <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R1&gt;, &lt;R2&gt;, &lt;R3&gt;</code> etc.
                </p>
                <p className="text-gray-700 text-base leading-relaxed">
                  Referring to the table of field names below, if a form item is required, it is marked with <ReqBadge value="Y" />, <ReqBadge value="N" /> is not required, &amp; <ReqBadge value="Y/AVS" /> means the field is only required if AVS is enabled on the merchant's account.
                </p>
              </section>

              {/* Available Form Fields */}
              <section id="available-form-fields" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-4">
                  Below is a table containing all the available fields for passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the Sale request.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse" data-testid="form-fields-table">
                    <thead>
                      <tr className="bg-[#1F4E79] text-white">
                        <th className="text-left p-3 w-20">Field</th>
                        <th className="text-left p-3">Description</th>
                        <th className="text-left p-3 w-24">Required</th>
                        <th className="text-left p-3 w-28">Definition</th>
                      </tr>
                    </thead>
                    <tbody>
                      {FORM_FIELDS.map((f, i) => (
                        <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                          <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                          <td className="p-3">{f.desc}</td>
                          <td className="p-3"><ReqBadge value={f.req} /></td>
                          <td className="p-3 font-mono text-xs">{f.def}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Validation */}
              <section id="validation" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields — Validation</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-4">
                  Below is a table containing all the available fields for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the Sale request including its validation regular expression. Use these when constructing the merchant's request data.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-[#1F4E79] text-white">
                        <th className="text-left p-3 w-20">Field</th>
                        <th className="text-left p-3">Description</th>
                        <th className="text-left p-3">Validation (Regex)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {VALIDATION.map((v, i) => (
                        <tr key={v.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                          <td className="p-3 font-mono font-semibold text-[#1F4E79]">{v.name}</td>
                          <td className="p-3">{v.desc}</td>
                          <td className="p-3 font-mono text-xs break-all">{v.regex}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Sample XML */}
              <section id="sample-xml" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-2xl font-mono">Sale</code> XML Document</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-4">
                  The below sample demonstrates what is expected when passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter.
                </p>
                <CodeBlock code={SALE_XML} lang="xml" testid="sale-xml-code" />
              </section>

              {/* Data Returned */}
              <section id="data-returned" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Data Returned</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-3">
                  The expected XML fields returned from the merchant's request depend on what way the merchant has requested the notification method to be set up, and if the provided card is 3D Secure.
                </p>
                <p className="text-gray-700 text-base leading-relaxed mb-3">
                  The merchant will receive <strong className="text-[#c32222]">0008 — Confirmation received</strong> if the notification method is set to "Notification". Otherwise, the transaction result <strong className="text-[#c32222]">0044 — Successful Transaction</strong> will return directly in the server response.
                </p>
                <p className="text-gray-700 text-base leading-relaxed">
                  If the provided card is 3D Secure, the merchant will receive <strong className="text-[#c32222]">0000 — Successful</strong> within the server response and must then cater for 3D Secure.
                </p>

                {/* 3DS Response */}
                <div id="response-3ds" className="scroll-mt-28 mt-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Response — Card Is 3D Secure</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-[#1F4E79] text-white">
                          <th className="text-left p-3 w-20">Field</th>
                          <th className="text-left p-3">Description</th>
                          <th className="text-left p-3 w-28">Definition</th>
                        </tr>
                      </thead>
                      <tbody>
                        {RESP_3DS_FIELDS.map((f, i) => (
                          <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                            <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                            <td className="p-3">{f.desc}</td>
                            <td className="p-3 font-mono text-xs">{f.def}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Transaction Result */}
                <div id="response-result" className="scroll-mt-28 mt-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Response — Transaction Result</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-[#1F4E79] text-white">
                          <th className="text-left p-3 w-20">Field</th>
                          <th className="text-left p-3">Description</th>
                          <th className="text-left p-3 w-28">Definition</th>
                        </tr>
                      </thead>
                      <tbody>
                        {RESP_RESULT_FIELDS.map((f, i) => (
                          <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                            <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                            <td className="p-3">{f.desc}</td>
                            <td className="p-3 font-mono text-xs">{f.def}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>

              {/* Expected XML Responses */}
              <section id="expected-responses" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Expected XML Documents</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-4">
                  This section demonstrates the expected XML document responses returned from the HTTP POST once the request has passed validation.
                </p>

                <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-5 mb-6">
                  <p className="font-semibold text-[#1F4E79] mb-3">Expected error code responses:</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-white/70">
                          <th className="text-left p-2 w-20 border-b border-blue-200 font-semibold">Code</th>
                          <th className="text-left p-2 border-b border-blue-200 font-semibold">Description</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-700">
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0000</td><td className="p-2">The card is 3D Secure. The merchant must now cater for 3D Secure to continue processing this payment.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0008</td><td className="p-2">Notification method set to "Notification". Confirmation will be sent to the Notification URL. <strong>This does not</strong> mean the transaction was successful or declined — the transaction result will be in the notification sent.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0044</td><td className="p-2">The transaction was successful.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0057</td><td className="p-2">Vendor Id disabled.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0058</td><td className="p-2">Token not registered.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0059</td><td className="p-2">Merchant token already registered.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0060</td><td className="p-2">Token Registered.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0061</td><td className="p-2">Pay Token Received.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0063</td><td className="p-2">Previous Transaction was not successful.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0064</td><td className="p-2">Transaction was already Voided.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0066</td><td className="p-2">Void Reason chosen does not match the Card Type voiding reason.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0067</td><td className="p-2">Vendor presented is not a VID/TID.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0069</td><td className="p-2">Vendor Blocked Bin Country.</td></tr>
                        <tr className="border-b border-blue-100"><td className="p-2 font-mono text-[#c32222] font-semibold">0075</td><td className="p-2">Multiple Recurring Billing Transactions being processed with same RBID.</td></tr>
                        <tr><td className="p-2 font-mono text-[#c32222] font-semibold">0076</td><td className="p-2">Vendor is not allowed to process MOTO transactions.</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2">Sample XML Response — Card Is 3D Secure (0000)</h3>
                <CodeBlock code={RESP_3DS} lang="xml" testid="resp-3ds-code" />

                <h3 className="text-lg font-bold text-gray-900 mb-2 mt-6">Sample XML Response — Notification Method Set: Notification (0008)</h3>
                <CodeBlock code={RESP_CONFIRM} lang="xml" testid="resp-confirm-code" />

                <h3 className="text-lg font-bold text-gray-900 mb-2 mt-6">Sample XML Response — Transaction Result (0044)</h3>
                <CodeBlock code={RESP_RESULT} lang="xml" testid="resp-result-code" />
              </section>

              {/* Transaction Lookup */}
              <section id="transaction-lookup" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Transaction Lookup</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-4">
                  The Transaction Lookup API allows merchants and integrated systems to retrieve transaction details using a unique <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">transactionId</code>. This endpoint returns key information about the transaction status, amount, card scheme data, chargebacks, and more.
                </p>

                {/* Endpoint & Auth */}
                <div id="lookup-endpoint" className="scroll-mt-28 mt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Endpoint</h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <p className="text-sm font-mono break-all">
                      <span className="inline-block px-2 py-0.5 bg-green-100 text-green-800 text-xs font-bold rounded mr-2">GET</span>
                      /api/TransactionLookupAPI/api/MerchantTransactionLookup/GetTransactionLookup?transactionId=&#123;transactionId&#125;
                    </p>
                  </div>

                  <h4 className="text-base font-bold text-gray-900 mb-2">Example Request</h4>
                  <CodeBlock
                    code={`GET /api/TransactionLookupAPI/api/MerchantTransactionLookup/GetTransactionLookup?transactionId=066dd1ad-4441-44b61-e39c6a4b5e2c`}
                    lang="http"
                    testid="lookup-request-code"
                  />

                  <h4 className="text-base font-bold text-gray-900 mb-2 mt-6">Authentication</h4>
                  <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700 mb-2"><strong>Authorization Type:</strong> Basic Authentication (HTTP Basic Auth)</p>
                    <p className="text-sm text-gray-700 mb-2">Provide:</p>
                    <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                      <li><strong>Username</strong></li>
                      <li><strong>Password</strong></li>
                    </ul>
                    <p className="text-sm text-gray-700 mt-2">The API will validate the credentials before returning transaction data.</p>
                  </div>
                </div>

                {/* Response & Fields */}
                <div id="lookup-response" className="scroll-mt-28 mt-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Response Format</h3>
                  <p className="text-gray-700 text-sm mb-3">The API returns a JSON array containing one or more transaction result objects.</p>

                  <h4 className="text-base font-bold text-gray-900 mb-2">Example Response</h4>
                  <CodeBlock
                    code={`[
  {
    "errorCode": "0044",
    "errorDescription": "Successful Transaction",
    "transactionStatus": "Transaction successful",
    "transactionType": "Sale",
    "schemeTransactionId": null,
    "amount": 66720,
    "amountRefunded": 0,
    "chargebackAmount": null,
    "arn": "15234706070800118402342",
    "vendorId": "10005631",
    "recurringBillingID": null,
    "declineReason": "Transaction successful",
    "issuerResponseCode": "Approved or completed successfully"
  }
]`}
                    lang="json"
                    testid="lookup-response-code"
                  />

                  <h4 className="text-base font-bold text-gray-900 mb-2 mt-6">Response Fields</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-[#1F4E79] text-white">
                          <th className="text-left p-3">Field Name</th>
                          <th className="text-left p-3">Type</th>
                          <th className="text-left p-3">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { n: 'errorCode', t: 'string', d: 'API status code indicating success or error during lookup.' },
                          { n: 'errorDescription', t: 'string', d: 'Description of the transaction status or error code.' },
                          { n: 'transactionStatus', t: 'string', d: 'High-level status of the transaction (e.g., Transaction successful).' },
                          { n: 'transactionType', t: 'string', d: 'Type of transaction such as Sale, Refund, Void, etc.' },
                          { n: 'schemeTransactionId', t: 'string / null', d: 'Card scheme (Visa / Mastercard) reference, if available.' },
                          { n: 'amount', t: 'integer', d: 'Transaction amount in minor units (e.g., cents).' },
                          { n: 'amountRefunded', t: 'integer', d: 'Total refunded amount for the transaction.' },
                          { n: 'chargebackAmount', t: 'integer / null', d: 'Amount involved in any chargeback, if applicable.' },
                          { n: 'arn', t: 'string', d: 'Acquirer Reference Number used for bank traceability.' },
                          { n: 'vendorId', t: 'string', d: 'Merchant / vendor identifier.' },
                          { n: 'recurringBillingID', t: 'string / null', d: 'Identifier for recurring billing transactions.' },
                          { n: 'declineReason', t: 'string', d: 'Reason provided by issuer or gateway for decline or success.' },
                          { n: 'issuerResponseCode', t: 'string', d: 'Issuer-provided response code or message.' },
                        ].map((f, i) => (
                          <tr key={f.n} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                            <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.n}</td>
                            <td className="p-3 font-mono text-xs">{f.t}</td>
                            <td className="p-3">{f.d}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <h4 className="text-base font-bold text-gray-900 mb-2 mt-6">Success Criteria</h4>
                  <p className="text-sm text-gray-700 mb-2">A successful transaction lookup typically returns:</p>
                  <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1 mb-4">
                    <li><code className="bg-gray-100 px-1 rounded">errorCode</code> = <code className="bg-gray-100 px-1 rounded">"0044"</code></li>
                    <li><code className="bg-gray-100 px-1 rounded">errorDescription</code> = <code className="bg-gray-100 px-1 rounded">"Successful Transaction"</code></li>
                  </ul>
                  <p className="text-sm text-gray-700 mb-2">Other error codes may indicate issues such as:</p>
                  <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1 mb-6">
                    <li>Invalid transaction ID</li>
                    <li>Unauthorized request</li>
                    <li>Transaction not found</li>
                  </ul>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-gray-800 mb-2">Notes</p>
                    <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                      <li>Amounts are returned in minor units. Example: <code className="bg-white px-1 rounded">66720</code> = €667.20</li>
                      <li>Not all fields will have values — some may return <code className="bg-white px-1 rounded">null</code> depending on the transaction type and issuer response.</li>
                      <li>The API may return multiple records if the <code className="bg-white px-1 rounded">transactionId</code> references more than one related transaction (rare but possible).</li>
                    </ul>
                  </div>
                </div>

                {/* Request Reference — Transaction Lookup */}
                <div id="lookup-request-reference" className="scroll-mt-28 mt-10">
                  <TransactionLookupPlayground />
                </div>
              </section>

              <ContainerPagination currentId="sale-pre-auth" />
            </main>

            {/* ============ STICKY SIDEBAR ============ */}
            <aside className="hidden lg:block lg:w-64 lg:flex-shrink-0 lg:order-2" data-testid="sale-preauth-sidebar">
              <div className="lg:sticky lg:top-28">
                <div className="bg-white/85 backdrop-blur-sm rounded-lg shadow-md border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 bg-[#1F4E79] text-white">
                    <p className="text-sm font-semibold tracking-wide">On This Page</p>
                  </div>
                  <nav className="py-2 max-h-[calc(100vh-10rem)] overflow-y-auto">
                    {TOC.map((item) => (
                      <div key={item.id}>
                        <a
                          href={`#${item.id}`}
                          onClick={(e) => handleNavClick(e, item.id)}
                          className={`block px-4 py-2 text-sm transition-colors duration-150 border-l-2 ${
                            activeId === item.id
                              ? 'border-[#1F4E79] bg-blue-50 text-[#1F4E79] font-semibold'
                              : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                          }`}
                          data-testid={`toc-link-${item.id}`}
                        >
                          {item.label}
                        </a>
                        {item.children && (
                          <div className="ml-3">
                            {item.children.map((child) => (
                              <a
                                key={child.id}
                                href={`#${child.id}`}
                                onClick={(e) => handleNavClick(e, child.id)}
                                className={`block px-4 py-1.5 text-xs transition-colors duration-150 border-l-2 ${
                                  activeId === child.id
                                    ? 'border-[#1F4E79] bg-blue-50/60 text-[#1F4E79] font-semibold'
                                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79]'
                                }`}
                                data-testid={`toc-link-${child.id}`}
                              >
                                {child.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </nav>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <KeyboardShortcutsOverlay />
      <BackToTop />
    </div>
  );
};

export default SalePreAuth;
